import { useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useWatchlist } from "@/hooks/useUserTokens";
import { trackEvent } from "@/lib/trackEvent";
import { cn } from "@/lib/utils";

/**
 * Pin/star control on token cards. Signed in -> writes to user_watchlist.
 * Signed out -> shows a lightweight "sign in to save this" prompt.
 */
export const WatchlistPinButton = ({
  symbol,
  className,
}: {
  symbol: string;
  className?: string;
}) => {
  const { user } = useAuth();
  const { isPinned, toggle, isToggling } = useWatchlist();
  const [promptOpen, setPromptOpen] = useState(false);
  const pinned = !!user && isPinned(symbol);

  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      setPromptOpen(true);
      trackEvent("watchlist_signin_prompt", { symbol });
      return;
    }

    try {
      const res = await toggle(symbol);
      toast.success(res.pinned ? `${res.symbol} added to your watchlist` : `${res.symbol} removed`);
      trackEvent(res.pinned ? "watchlist_added" : "watchlist_removed", { symbol: res.symbol });
    } catch {
      toast.error("Couldn't update your watchlist. Please try again.");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={isToggling}
        aria-label={pinned ? `Remove ${symbol} from watchlist` : `Save ${symbol} to watchlist`}
        aria-pressed={pinned}
        className={cn(
          "p-1 rounded-md transition-colors hover:bg-secondary/60",
          pinned ? "text-warning" : "text-muted-foreground hover:text-foreground",
          className,
        )}
      >
        <Star className="w-4 h-4" fill={pinned ? "currentColor" : "none"} />
      </button>

      <Dialog open={promptOpen} onOpenChange={setPromptOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Sign in to save this</DialogTitle>
            <DialogDescription>
              Create a free account to pin {symbol.toUpperCase()} to your watchlist and see it —
              with live prices — on any device you use.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button asChild className="flex-1">
              <Link to="/auth" onClick={() => setPromptOpen(false)}>
                Sign in
              </Link>
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => setPromptOpen(false)}>
              Not now
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Everything on GoToCoins stays free to use without an account.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WatchlistPinButton;
